import { useState } from 'react';
import {
  Box,
  VStack,
  Text,
  useToast,
  Progress,
  chakra,
  keyframes,
  useColorModeValue,
  Stack,
  Flex,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiEye, FiEyeOff, FiCheck } from 'react-icons/fi';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useHandleSendData } from '../../../services/hooks/useHandleSendData';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import { HiMiniLockClosed } from 'react-icons/hi2';
import CardHeaderMobile from '../../../components/Mobile/header';
import NavigationButtonMobilePage from '../../../components/Mobile/navigationButton';
import InputMobile from '../../../components/Input/Mobile';
import { FaLock, FaUnlockAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';

interface IUpdatePassword {
  old_password: string;
  new_password: string;
  confirm_new_password: string;
}

const floatIn = keyframes`
  from { opacity: 0; transform: translateY(18px) scale(0.995); }
  to   { opacity: 1; transform: translateY(0px) scale(1); }
`;
const AnimatedBox = chakra(Box, {
  baseStyle: {
    animation: `${floatIn} 420ms cubic-bezier(.2,.9,.3,1) both`,
  },
});

const MotionBox = motion(Box);

function passwordStrength(password: string) {
  let score = 0;
  if (!password) return { score, label: 'Muito fraca' };
  const lengthRule = password.length >= 8;
  const upperRule = /[A-Z]/.test(password);
  const lowerRule = /[a-z]/.test(password);
  const numberRule = /[0-9]/.test(password);
  const specialRule = /[^A-Za-z0-9]/.test(password);

  score += lengthRule ? 25 : Math.min(20, password.length * 2);
  score += upperRule ? 15 : 0;
  score += lowerRule ? 10 : 0;
  score += numberRule ? 25 : 0;
  score += specialRule ? 25 : 0;
  score = Math.min(100, Math.round(score));

  let label = 'Muito fraca';
  if (score >= 80) label = 'Excelente';
  else if (score >= 60) label = 'Boa';
  else if (score >= 40) label = 'Média';
  else if (score >= 20) label = 'Muito fraca';

  return { score, label };
}

export default function PasswordChangePage() {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirmNew, setShowConfirmNew] = useState(false);

  const validateForm = Yup.object().shape({
    old_password: Yup.string().required('O campo é obrigatório.'),
    new_password: Yup.string().required('O campo é obrigatório.').min(8, 'A senha não pode ter menos de 8 caracteres.'),
    confirm_new_password: Yup.string()
      .required('O campo é obrigatório.')
      .oneOf([Yup.ref('new_password')], 'As senhas devem ser iguais.'),
  });

  const hookform = useForm({
    resolver: yupResolver(validateForm),
  });

  const navigate = useNavigate();

  const { isPending, mutateAsync } = useHandleSendData<IUpdatePassword>({
    endPoint: 'auth/change-password',
    keyRequest: 'update-password',
  });

  const newPassword = hookform.watch('new_password');

  const toast = useToast();
  const { score, label } = passwordStrength(newPassword);

  const accent = useColorModeValue('teal.500', 'teal.300');
  const muted = useColorModeValue('gray.600', 'gray.300');

  const changePasswordRequest = (data: FieldValues) => {
    mutateAsync({
      old_password: data.old_password,
      new_password: data.new_password,
      confirm_new_password: data.confirm_new_password,
    })
      .then(() => {
        toast({
          title: 'Senha atualizada',
          description: 'Sua senha foi alterada com sucesso.',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        navigate('/painel/inscricoes');
      })
      .catch((error) => {
        const errorMessage = error.response.data.message;
        if (error.response && error.response.status === 400) {
          toast({
            title: 'Erro',
            description: errorMessage,
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Erro',
            description: 'Erro na solicitação, tente novamente mais tarde',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        }
      });
  };

  const bgGradient = useColorModeValue('linear(to-br, gray.50, white)', 'linear(to-br, gray.900, gray.800)');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  return (
    <Stack as="form" h="100dvh" onSubmit={hookform.handleSubmit(changePasswordRequest)}>
      <Stack height="calc(100% - 40px)" w="100%" top={0} bg="white" gap={0}>
        <Header />
        <VStack mt={0} flex={1} overflowY="auto" py={2}>
          <AnimatedBox
            bgGradient={bgGradient}
            borderRadius="lg"
            boxShadow="lg"
            mx={3}
            p={{ base: 5, md: 10 }}
            borderWidth={'1px'}
            borderColor={borderColor}
            bg="white"
            flex={1}
            display="flex"
          >
            <VStack boxSizing="border-box" maxW="lg" spacing={4} align="stretch">
              <CardHeaderMobile
                title="Alterar senha"
                subTitle="Proteja sua conta com uma senha forte — nunca compartilhe com ninguém."
                badgeText="Atualização segura"
                badgeIcon={<HiMiniLockClosed size={12} />}
                colorScheme="teal"
              />

              <VStack spacing={6} mt={5} align="stretch" mx={4}>
                <FormProvider {...hookform}>
                  <InputMobile
                    name="old_password"
                    placeholderLabel="Senha atual"
                    iconLeft={<FaUnlockAlt size={12} color={accent} />}
                    isRequired
                    type={showCurrent ? 'text' : 'password'}
                    aria-label={showCurrent ? 'Esconder' : 'Mostrar'}
                    autoComplete="old_password"
                    onClickRightIcon={() => setShowCurrent((e) => !e)}
                    iconRight={showCurrent ? <FiEyeOff /> : <FiEye />}
                  />
                  <Box>
                    <InputMobile
                      name="new_password"
                      placeholderLabel="Nova senha"
                      iconLeft={<FaLock size={12} color={accent} />}
                      isRequired
                      type={showNew ? 'text' : 'password'}
                      aria-label={showNew ? 'Esconder' : 'Mostrar'}
                      autoComplete="new_password"
                      onClickRightIcon={() => setShowNew((e) => !e)}
                      iconRight={showNew ? <FiEyeOff /> : <FiEye />}
                    />
                    <Stack justify="space-between" mt={3} spacing={4}>
                      {newPassword && (
                        <Box flex={1}>
                          <Progress
                            value={score}
                            size="sm"
                            borderRadius="sm"
                            aria-label={`Força da senha: ${label}`}
                            sx={{
                              '& > div': {
                                width: '100%',
                                bg: score >= 80 ? 'green.400' : score >= 60 ? 'yellow.400' : 'red.400',
                              },
                            }}
                          />
                          <Text mt={2} fontSize="sm" color={muted}>
                            Força:{' '}
                            <Text as="span" color={accent} fontWeight="semibold">
                              {label}
                            </Text>
                          </Text>
                        </Box>
                      )}

                      <Box minW="120px">
                        <Text fontWeight="bold" fontSize="sm" color={muted}>
                          Requisitos:
                        </Text>
                        <Stack ml={2} mt={2} fontSize="xs" lineHeight={0.9} color={muted}>
                          <Flex gap={1} align="center">
                            {newPassword?.length > 7 ? (
                              <FiCheck style={{ display: 'inline' }} size={14} />
                            ) : (
                              <Text as="span">•</Text>
                            )}
                            <Text transition="0.4s" color={newPassword?.length > 7 ? 'green' : undefined}>
                              Mínimo 8 caracteres
                            </Text>
                          </Flex>
                          <Flex gap={1} align="center">
                            {/(?=.*[a-z])(?=.*[A-Z])/.test(newPassword) ? (
                              <FiCheck style={{ display: 'inline' }} size={14} />
                            ) : (
                              <Text as="span">•</Text>
                            )}
                            <Text
                              transition="0.4s"
                              color={/(?=.*[a-z])(?=.*[A-Z])/.test(newPassword) ? 'green' : undefined}
                            >
                              Letras maiúsculas e minúsculas
                            </Text>
                          </Flex>
                          <Flex gap={1} align="center">
                            {/[0-9]/.test(newPassword) ? (
                              <FiCheck style={{ display: 'inline' }} size={14} />
                            ) : (
                              <Text as="span">•</Text>
                            )}
                            <Text transition="0.4s" color={/[0-9]/.test(newPassword) ? 'green' : undefined}>
                              Números
                            </Text>
                          </Flex>
                          <Flex gap={1} align="center">
                            {/(?=.*[^A-Za-z0-9])/.test(newPassword) ? (
                              <FiCheck style={{ display: 'inline' }} size={14} />
                            ) : (
                              <Text as="span">•</Text>
                            )}
                            <Text
                              transition="0.4s"
                              color={/(?=.*[^A-Za-z0-9])/.test(newPassword) ? 'green' : undefined}
                            >
                              Caracteres especiais (Recomendado)
                            </Text>
                          </Flex>
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                  <InputMobile
                    name="confirm_new_password"
                    placeholderLabel="Confirmar nova senha"
                    iconLeft={<FaLock size={12} color={accent} />}
                    isRequired
                    type={showConfirmNew ? 'text' : 'password'}
                    aria-label={showConfirmNew ? 'Esconder' : 'Mostrar'}
                    autoComplete="confirm_new_password"
                    onClickRightIcon={() => setShowConfirmNew((e) => !e)}
                    iconRight={showConfirmNew ? <FiEyeOff /> : <FiEye />}
                  />
                </FormProvider>
              </VStack>
              <MotionBox
                initial={{ y: -8, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                mt={5}
              >
                <Alert status="info" variant="left-accent" p={2}>
                  <AlertIcon />
                  <Text>
                    Se quiser apagar tudo e começar de novo, use o botão <strong>Limpar</strong> logo abaixo. Isso não
                    afeta dados salvos até você confirmar.
                  </Text>
                </Alert>
              </MotionBox>
            </VStack>
          </AnimatedBox>
        </VStack>
      </Stack>
      <NavigationButtonMobilePage
        absolute
        mainButtonText="Salvar nova senha"
        typeButton="submit"
        isLoading={isPending}
        goBackButtonText="Limpar"
        onGoBackButton={() => {
          hookform.reset({
            old_password: '',
            new_password: '',
            confirm_new_password: '',
          });
        }}
      />
    </Stack>
  );
}
